import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, User, FileText, Download } from "lucide-react";
import { api, API_BASE as API } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { DashboardHeader } from "@/components/DashboardHeader";

const statusStyle = {
  sent: "bg-blue-100 text-blue-800",
  accepted: "bg-teal-100 text-teal-800",
  advance_paid: "bg-amber-100 text-amber-800",
  service_completed: "bg-purple-100 text-purple-800",
  final_paid: "bg-green-100 text-green-800",
  cancelled: "bg-slate-200 text-slate-700",
};
const statusLabel = (s) =>
  ({ sent: "Sent", accepted: "Accepted", advance_paid: "Advance paid", service_completed: "Service complete", final_paid: "Paid in full", cancelled: "Cancelled" }[s] || s);

const AdminTabs = ({ active }) => (
  <div className="flex flex-wrap gap-2 text-sm">
    <Link to="/boxbquote" data-testid="tab-quotes" className={`rounded-full px-4 py-1.5 font-semibold ${active === "quotes" ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 hover:border-[#0A192F] text-[#0A192F]"}`}>Quotes</Link>
    <Link to="/boxbquote/customers" data-testid="tab-customers" className={`rounded-full px-4 py-1.5 font-semibold ${active === "customers" ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 hover:border-[#0A192F] text-[#0A192F]"}`}>Customers</Link>
    <Link to="/boxbquote/invoices" data-testid="tab-invoices" className={`rounded-full px-4 py-1.5 font-semibold ${active === "invoices" ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 hover:border-[#0A192F] text-[#0A192F]"}`}>Invoices</Link>
    <Link to="/boxbquote/enquiries" className="rounded-full bg-white border border-slate-200 hover:border-[#0A192F] px-4 py-1.5 font-semibold text-[#0A192F]">Enquiries</Link>
  </div>
);

export const AdminCustomers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/customers"); setRows(data); }
      catch { toast.error("Failed to load customers"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(r =>
      (r.email || "").toLowerCase().includes(s) ||
      (r.name || "").toLowerCase().includes(s) ||
      (r.phone || "").toLowerCase().includes(s) ||
      (r.address || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  return (
    <div data-testid="admin-customers" className="min-h-screen bg-[#F8FAFC]">
      <Seo title="Admin · Customers" description="Admin customer directory" canonical="/boxbquote/customers" noindex />
      <DashboardHeader title="Customers" subtitle="Every person who's received a quote or invoice, grouped by email." />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 pb-16 space-y-6">
        <AdminTabs active="customers" />
        <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-wrap items-center gap-3 justify-between">
            <h2 className="font-display font-semibold text-xl text-[#0A192F]">{filtered.length} customer{filtered.length === 1 ? "" : "s"}</h2>
            <input data-testid="customer-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search name, email, phone, address"
                   className="rounded-full border border-slate-200 px-4 py-2 text-sm w-full sm:w-72 outline-none focus:border-[#0A192F]" />
          </div>
          {loading ? <div className="p-10 text-center text-slate-500">Loading…</div>
          : filtered.length === 0 ? <div className="p-10 text-center text-slate-500" data-testid="customers-empty">No customers found.</div>
          : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <li key={c.email} data-testid={`customer-${c.email}`} className="p-5">
                  <button onClick={() => setExpanded({ ...expanded, [c.email]: !expanded[c.email] })}
                          className="w-full text-left grid md:grid-cols-12 gap-3 items-center">
                    <div className="md:col-span-4 min-w-0 flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 rounded-full bg-slate-100 items-center justify-center text-[#0A192F]"><User className="h-4 w-4" /></span>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-[#0A192F] truncate">{c.name || c.email}</div>
                        <div className="text-xs text-slate-500 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                      </div>
                    </div>
                    <div className="md:col-span-3 text-sm">
                      <div className="text-slate-500 text-xs uppercase tracking-widest font-bold">Activity</div>
                      <div className="text-[#0A192F] font-semibold">{c.quotes_count} quote{c.quotes_count === 1 ? "" : "s"} · {c.invoices_count} invoice{c.invoices_count === 1 ? "" : "s"}</div>
                    </div>
                    <div className="md:col-span-3 text-sm">
                      <div className="text-slate-500 text-xs uppercase tracking-widest font-bold">Billed / Paid</div>
                      <div className="text-[#0A192F] font-semibold">£{Number(c.total_billed).toFixed(2)} · <span className="text-green-700">£{Number(c.total_paid).toFixed(2)}</span></div>
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                      <span className="text-xs text-[#2563EB] font-semibold">{expanded[c.email] ? "Hide" : "View history"}</span>
                    </div>
                  </button>

                  {expanded[c.email] && (
                    <div data-testid={`history-${c.email}`} className="mt-4 rounded-2xl bg-[#F8FAFC] border border-slate-200 p-4 space-y-2">
                      {c.address && <div className="text-xs text-slate-500"><b>Address:</b> {c.address}</div>}
                      {c.history.map((h) => (
                        <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-t border-slate-100 first:border-t-0">
                          <div className="text-sm">
                            <span className="text-[#0A192F] font-semibold">
                              {h.is_invoice ? h.invoice_number : "Quote"} · {h.quote_type === "hourly" ? "Hourly" : "Fixed"}
                            </span>
                            <span className="ml-2 text-xs text-slate-500">{new Date(h.created_at).toLocaleDateString("en-GB")}</span>
                          </div>
                          <div className="text-sm">£{Number(h.total_pounds).toFixed(2)}</div>
                          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusStyle[h.status] || "bg-slate-100"}`}>{statusLabel(h.status)}</span>
                          <Link to={`/boxbquote/quotes/${h.id}`} className="text-xs text-[#2563EB] hover:underline">Open →</Link>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdminInvoices = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/invoices"); setRows(data); }
      catch { toast.error("Failed to load invoices"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(r =>
      (r.invoice_number || "").toLowerCase().includes(s) ||
      (r.customer_name || "").toLowerCase().includes(s) ||
      (r.customer_email || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  return (
    <div data-testid="admin-invoices" className="min-h-screen bg-[#F8FAFC]">
      <Seo title="Admin · Invoices" description="Admin invoices" canonical="/boxbquote/invoices" noindex />
      <DashboardHeader title="Invoices" subtitle="All invoices you've issued, latest first." />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 pb-16 space-y-6">
        <AdminTabs active="invoices" />
        <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-wrap items-center gap-3 justify-between">
            <h2 className="font-display font-semibold text-xl text-[#0A192F]">{filtered.length} invoice{filtered.length === 1 ? "" : "s"}</h2>
            <input data-testid="invoice-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search invoice #, name, email"
                   className="rounded-full border border-slate-200 px-4 py-2 text-sm w-full sm:w-72 outline-none focus:border-[#0A192F]" />
          </div>
          {loading ? <div className="p-10 text-center text-slate-500">Loading…</div>
          : filtered.length === 0 ? <div className="p-10 text-center text-slate-500" data-testid="invoices-empty">No invoices yet.</div>
          : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((q) => (
                <li key={q.id} data-testid={`invoice-${q.id}`} className="p-5 grid md:grid-cols-12 gap-3 items-center">
                  <div className="md:col-span-1">
                    <span className="inline-flex h-9 w-9 rounded-full bg-purple-50 items-center justify-center text-purple-700"><FileText className="h-4 w-4" /></span>
                  </div>
                  <div className="md:col-span-4 min-w-0">
                    <div className="font-display font-semibold text-[#0A192F] truncate">{q.invoice_number}</div>
                    <div className="text-xs text-slate-500 truncate">{q.customer_name} · {q.customer_email}</div>
                  </div>
                  <div className="md:col-span-2 text-sm">
                    <div className="text-slate-500 text-xs uppercase tracking-widest font-bold">Issued</div>
                    <div className="text-[#0A192F]">{q.invoice_issued_at ? new Date(q.invoice_issued_at).toLocaleDateString("en-GB") : "—"}</div>
                  </div>
                  <div className="md:col-span-2 text-sm">
                    <div className="text-slate-500 text-xs uppercase tracking-widest font-bold">Total</div>
                    <div className="text-[#0A192F] font-semibold">£{Number(q.total_pounds).toFixed(2)}</div>
                  </div>
                  <div className="md:col-span-2">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusStyle[q.status] || "bg-slate-100 text-slate-700"}`}>{statusLabel(q.status)}</span>
                  </div>
                  <div className="md:col-span-1 flex justify-end gap-2">
                    <a href={`${API}/admin/quotes/${q.id}/invoice.pdf`} target="_blank" rel="noopener noreferrer"
                       data-testid={`invoice-pdf-${q.id}`} className="rounded-full bg-slate-100 hover:bg-slate-200 p-2 text-[#0A192F]" title="Download PDF">
                      <Download className="h-4 w-4" />
                    </a>
                    <Link to={`/boxbquote/quotes/${q.id}`} className="rounded-full bg-slate-100 hover:bg-slate-200 px-3 py-1.5 text-xs font-semibold text-[#0A192F]">Open</Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
